import { CircularProgress } from '@mui/material';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { Route, Switch, useRoute } from 'wouter';
import * as appStyles from './app.css';
import { Create } from './components/Create/Create';
import { DebugControls } from './components/DebugControls/DebugControls';
import { Header } from './components/Header/Header';
import { HeaderMobile } from './components/HeaderMobile/HeaderMobile';
import { Invite } from './components/Invite/Invite';
import Page from './components/Page/Page';
import { Payment } from './components/Payment/Payment';
import { SearchMobile } from './components/SearchMobile/SearchMobile';
import { Settings } from './components/Settings/Settings';
import { Sidebar } from './components/Sidebar/Sidebar';
import { Success } from './components/Success/Success';
import { Toast } from './components/Toast/Toast';
import Login from './Login';
import { colors, darkTheme, lightTheme } from './theme.css';
import { DEV, preview } from './utils/constants';
import { useAuthLoader, useFile, useKeyPress, useParams, useWikis } from './utils/hooks/hooks';
import { log } from './utils/logger';
import { useMobile, useStore, useTheme, useToggleTheme } from './utils/store';
import { setCookie } from './utils/utils';

const AppLoader = () => {
  const [gapiError, setGapiError] = useState<unknown>('');
  const [gapiLoaded, setGapiLoaded] = useState(window.gapi_loaded.loaded);
  const { isLoading: isAuthLoading, data: user, error: authError } = useAuthLoader();
  const path = window.location.pathname;
  const route = useRoute('/app/page/:wikiId/:pageId?')[1] ?? {
    pageId: undefined,
    wikiId: undefined,
  };
  const toggleTheme = useToggleTheme();

  // Dark/light mode
  useKeyPress('d', (e) => {
    if (e.ctrlKey) return;
    if (e.metaKey) return;
    if (e.shiftKey) return;
    toggleTheme();
  });

  useEffect(() => {
    if (gapi_loaded.loaded) return;
    gapi_loaded.promise.then(() => setGapiLoaded(true)).catch((err: unknown) => setGapiError(err));
  }, []);

  if (path === '/login') return <Login />;
  if (!gapiLoaded || isAuthLoading) return <App loading />;
  if (!user && !route.wikiId) return <Login />;

  if (DEV) {
    if (authError && Object.keys(authError).length > 0) log.error('Auth error:', authError);
    if (gapiError) log.error('gapi error:', gapiError);
  }
  return <App />;
};

const Container = ({
  children,
  theme,
  style,
}: {
  children: React.ReactNode;
  theme: 'dark' | 'light';
  style?: React.CSSProperties;
}) => (
  <div
    id="app"
    key="app"
    className={cx(appStyles.appContainer, {
      [darkTheme]: theme === 'dark',
      [lightTheme]: theme === 'light',
      preview,
      scrollbar: preview && document.body.scrollHeight > window.innerHeight,
    })}
    style={style}
  >
    {children}
    {DEV && <DebugControls />}
    <Toast />
  </div>
);

export const App = ({ loading }: { loading?: boolean }) => {
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMobile();
  const [isPageRoute] = useRoute(/\/$|\/app$|\/app\/page(.*)/);
  const { data: wikis, isLoading: wikisLoading } = useWikis();
  const { data: wiki, isLoading: wikiLoading } = useFile(
    loading || wikis.length > 0 ? undefined : params.wikiId,
    [],
    params.wikiId
  );
  const editing = useStore((state) => state.editing);

  useKeyPress('Escape', () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // useEffect(() => {
  //   if (user) {
  //     posthog.identify(user.id, {
  //       email: user.email,
  //       name: user.name,
  //     });
  //   }
  // }, [user]);

  useEffect(() => {
    if (wiki?.id) {
      setCookie({
        name: 'wikiId',
        value: wiki.id,
        path: '/',
        expires: Date.now() + 31536000000,
        sameSite: 'strict',
      });
    }
  }, [wiki?.id]);

  const { fontColor } = wiki?.properties ?? {};
  const showSpinner = loading || wikisLoading || wikiLoading;

  // log.dev('app:', {
  //   loading,
  //   wikiLoading,
  //   wikisLoading,
  //   wikisLength: wikis.length,
  //   wiki,
  //   paramsWikiId: params.wikiId,
  //   showSpinner,
  // });

  if (showSpinner) {
    return (
      <Container theme={theme}>
        <div className={cx(appStyles.flex, appStyles.justifyCenter, appStyles.wh100)} style={{ paddingTop: '2.5rem' }}>
          <CircularProgress
            key="progress"
            style={{
              color: theme === 'dark' ? colors.spinner.default : (fontColor ?? colors.spinner.default),
            }}
            thickness={4}
            size={34}
          />
        </div>
      </Container>
    );
  }

  if (!wiki && wikis.length === 0) {
    return (
      <Container theme={theme} style={isPageRoute ? { display: 'flex' } : {}}>
        <Create />
      </Container>
    );
  }
  if (!params.wikiId) return null;

  return (
    <Container theme={theme} style={isPageRoute ? { display: 'flex' } : {}}>
      <Switch>
        <Route path="/app/settings">
          <Settings wikiId={params.wikiId} />
        </Route>
        <Route path="/app/payment">
          <Payment />
        </Route>
        <Route path="/app/success">
          <Success />
        </Route>
        <Route path="/app/create">
          <Create />
        </Route>
        <Route path="/app/invite">
          <Invite wikiId={params.wikiId} />
        </Route>
        <Route path={/\/$|\/app$|\/app\/page(.*)/}>
          {isMobile && <HeaderMobile wikiId={params.wikiId} />}
          <Sidebar key="sidebar" wikiId={params.wikiId} id={params.pageId} />
          <div className={cx(appStyles.mainContent, { [appStyles.mainContentEditing]: editing })}>
            <Header wikiId={params.wikiId} id={params.pageId} />
            <SearchMobile wikiId={params.wikiId} />
            <Page wikiId={params.wikiId} id={params.pageId} />
          </div>
        </Route>
        <Route>Not found</Route>
      </Switch>
    </Container>
  );
};

export default AppLoader;
